import React from 'react'

import { Box, Chip, Divider, Grid, Typography } from '@mui/material'

import ReleaseItemButton from '../../../pages/AdminDashboard/components/Users/components/UserDetails/components/OrderDetails/components/ReleaseItemButton/ReleaseItemButton.component'
import Modal from '../Modal/Modal.component'

import date, { dateTimeFormats } from '../../../shared/functions/Date/date.functions'
import status from '../../../shared/functions/Status/status.dictionaries'
import { getStatusColor } from '../../../shared/functions/Status/status.functions'
import { formatMoney } from '../../../shared/functions/String/string.functions'
import linkStyles from '../../styles/link.styles'
import AppModal from '../Modal/Modal.functions'
import {
  deliveredToShopperStatuses,
  deliveredToTravelerAddressStatuses,
  purchasedByAdminStatuses
} from '../ReadyToPurchaseTable/ReadyToPurchaseTable.dictionary'
import styles from './ItemRequestActionModal.styles'
import { ItemRequestActionModalProps } from './ItemRequestActionModal.types'

const ItemRequestActionModal: React.FC<ItemRequestActionModalProps> = (props) => {
  const {
    open,
    onClose,
    itemRequest,
    onBuy,
    onConfirmItemBought,
    onConfirmDelivery,
    onDismiss,
    refreshTable,
    confirmItemBoughtLoading
  } = props

  const handleBuy = () => {
    window.open(itemRequest?.itemDetails?.url, '_blank')
    onBuy?.()
  }

  const confirmAction = (
    title: string,
    body: React.ReactNode,
    onConfirm?: () => void,
    confirmLabel: string = 'Confirm'
  ) => {
    AppModal.show({
      title,
      body,
      primaryButton: {
        label: confirmLabel,
        onClick: onConfirm
      },
      secondaryButton: {
        label: 'Cancel'
      }
    })
  }

  const modalBody = (
    <Box sx={styles.modalBody}>
      <Grid container spacing={3}>
        {/* Product Image */}
        <Grid item xs={12} md={4}>
          <Box sx={styles.imageContainer}>
            <Box
              component="img"
              src={itemRequest?.itemDetails?.images?.[0] || ''}
              alt={itemRequest?.itemDetails?.productName || ''}
              sx={styles.productImage}
            />
          </Box>
        </Grid>

        {/* Product Details */}
        <Grid item xs={12} md={8}>
          <Box sx={styles.productDetails}>
            <Typography variant="h6" sx={styles.productName}>
              {itemRequest.itemDetails.productName}
            </Typography>

            <Box sx={styles.productInfo}>
              <Typography variant="body2" color="text.secondary">
                Quantity: {itemRequest.quantity}
              </Typography>

              <Typography variant="body2" color="text.secondary">
                Price: {formatMoney(itemRequest.acceptedItemPrice, 'USD')}
              </Typography>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Divider />
        </Grid>

        {/* Delivery Details */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle1" sx={styles.sectionTitle}>
            Delivery Details
          </Typography>

          <Box sx={styles.deliveryInfo}>
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <Typography variant="body2" sx={styles.infoLabel}>
                Street Address:
              </Typography>

              <Typography variant="body2">
                {itemRequest.offerRequestDetails.deliveryDetails.deliveryAddress.street}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <Typography variant="body2" sx={styles.infoLabel}>
                City:
              </Typography>

              <Typography variant="body2">
                {itemRequest.offerRequestDetails.deliveryDetails.deliveryAddress.city}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <Typography variant="body2" sx={styles.infoLabel}>
                Country:
              </Typography>

              <Typography variant="body2">
                {itemRequest.offerRequestDetails.deliveryDetails.deliveryAddress.country}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <Typography variant="body2" sx={styles.infoLabel}>
                Postal Code:
              </Typography>

              <Typography variant="body2">
                {itemRequest.offerRequestDetails.deliveryDetails.deliveryAddress.postalCode}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 1 }}>
              <Typography variant="body2" sx={styles.infoLabel}>
                Delivery Window:
              </Typography>

              <Typography variant="body2">
                {`${date(itemRequest.offerRequestDetails.deliveryDetails.deliveryWindow.startDate).format(dateTimeFormats.date.medium)} - ${date(itemRequest.offerRequestDetails.deliveryDetails.deliveryWindow.endDate).format(dateTimeFormats.date.medium)}`}
              </Typography>
            </Box>
          </Box>
        </Grid>

        {/* Traveler Details */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle1" sx={styles.sectionTitle}>
            Traveler Details
          </Typography>

          <Box sx={styles.travelerInfo}>
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <Typography variant="body2" sx={styles.infoLabel}>
                Name:
              </Typography>

              <Typography variant="body2">
                {itemRequest.offerRequestDetails.travelerDetails.firstName}{' '}
                {itemRequest.offerRequestDetails.travelerDetails.lastName}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 1 }}>
              <Typography variant="body2" sx={styles.infoLabel}>
                Email:
              </Typography>

              <Typography variant="body2">
                <a href={`mailto:${itemRequest.offerRequestDetails.travelerDetails.email}`} style={linkStyles}>
                  {itemRequest.offerRequestDetails.travelerDetails.email}
                </a>
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  )

  // Determine which buttons to show based on item status
  const isReadyToPurchase = itemRequest.status?.name === status.itemRequestStatus.acceptedByTraveler
  const isPurchasedByAdmin = purchasedByAdminStatuses.includes(itemRequest.status?.name)
  const isDeliveredToTravelerAddress = deliveredToTravelerAddressStatuses.includes(itemRequest.status?.name)
  const isConfirmedReceivedByTraveler = itemRequest.status?.name === status.deliveryStatus.confirmedReceivedByTraveler
  const isDeliveredToShopper = deliveredToShopperStatuses.includes(itemRequest.status?.name)

  const isItemRequestAcceptedByTraveler = itemRequest.status?.name === status.itemRequestStatus.acceptedByTraveler
  const isItemRequestDeniedByTraveler = itemRequest.status?.name === status.itemRequestStatus.deniedByTraveler

  const getPrimaryButton = () => {
    if (isReadyToPurchase) {
      return {
        label: 'Buy',
        onClick: handleBuy,
        loading: false
      }
    } else if (isPurchasedByAdmin && !isConfirmedReceivedByTraveler) {
      return {
        label: 'Confirm Delivery to Traveler',
        onClick: () =>
          confirmAction(
            'Confirm Delivery',
            'Mark this item as delivered to the traveler? This updates tracking details for the order.',
            onConfirmDelivery
          ),
        loading: confirmItemBoughtLoading || false
      }
    } else if (isDeliveredToTravelerAddress || isDeliveredToShopper) {
      return {
        label: 'Dismiss',
        onClick: onDismiss,
        loading: false
      }
    }

    // Default to dismiss if status is unknown
    return {
      label: 'Dismiss',
      onClick: onDismiss,
      loading: false
    }
  }

  const getSecondaryButton = () => {
    if (isReadyToPurchase) {
      // Ready to purchase - show Confirm Item Bought button
      return {
        label: 'Confirm Item Bought',
        onClick: () =>
          confirmAction(
            'Confirm Item Bought',
            'Confirm you have purchased this item? This will update the item status to Purchased by Admin.',
            onConfirmItemBought
          ),
        loading: confirmItemBoughtLoading || false
      }
    } else if (isPurchasedByAdmin && !isDeliveredToTravelerAddress && !isDeliveredToShopper) {
      // Purchased but not delivered - show Dismiss button
      return {
        label: 'Dismiss',
        onClick: onDismiss,
        loading: false
      }
    }

    return undefined
  }

  const getTertiaryButton = () => {
    if (isReadyToPurchase) {
      // Ready to purchase - show Dismiss button as tertiary
      return {
        label: 'Dismiss',
        onClick: onDismiss,
        loading: false
      }
    }

    return undefined
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Item Purchase Details"
      headerRightSection={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip label={itemRequest.status?.displayName} color={getStatusColor(itemRequest.status?.name)} size="small" />

          {(isItemRequestAcceptedByTraveler || isItemRequestDeniedByTraveler) && (
            <ReleaseItemButton
              itemRequestId={itemRequest.id}
              itemRequest={itemRequest}
              onSuccess={() => {
                onClose()
                refreshTable?.()
              }}
            />
          )}
        </Box>
      }
      body={modalBody}
      primaryButton={getPrimaryButton()}
      secondaryButton={getSecondaryButton()}
      tertiaryButton={getTertiaryButton()}
      maxWidth="md"
    />
  )
}

export default ItemRequestActionModal
